import type { VMSnapshot } from "@/hooks/use-vm-live-usage";

export type HealthLevel = "low" | "medium" | "high" | "unknown";
export type ResourceMetricKey = "cpu" | "memory" | "disk";

export type ResourceMetric = {
    key: ResourceMetricKey;
    label: string;
    percent: number;
    level: Exclude<HealthLevel, "unknown">;
};

export const THRESHOLD_MEDIUM = 70;
export const THRESHOLD_HIGH = 90;

/**
 * Derives an overall health level from VM stats.
 * Evaluates CPU %, memory %, and disk % against shared thresholds.
 */
export function getHealthLevel(data: VMSnapshot | undefined): HealthLevel {
    if (!data) {
        return "unknown";
    }

    const metrics = getPressureMetrics(data);

    if (metrics.some((metric) => metric.level === "high")) {
        return "high";
    }
    if (metrics.some((metric) => metric.level === "medium")) {
        return "medium";
    }
    return "low";
}

export function getMetricHealthLevel(percent: number): Exclude<HealthLevel, "unknown"> {
    if (percent > THRESHOLD_HIGH) {
        return "high";
    }
    if (percent > THRESHOLD_MEDIUM) {
        return "medium";
    }
    return "low";
}

export function getPressureMetrics(data: VMSnapshot): ResourceMetric[] {
    const { vm } = data;
    const memPercent = vm.memory.total_bytes > 0 ? (vm.memory.used_bytes / vm.memory.total_bytes) * 100 : 0;

    // In dual-disk mode, only the data disk matters for health warnings —
    // the system disk is a small AMI volume that users don't control.
    const diskToCheck = vm.data_disk ?? vm.disk;
    const diskPercent = diskToCheck.total_bytes > 0 ? (diskToCheck.used_bytes / diskToCheck.total_bytes) * 100 : 0;

    return [
        {
            key: "cpu",
            label: "CPU",
            percent: vm.cpu.usage_percent,
            level: getMetricHealthLevel(vm.cpu.usage_percent),
        },
        {
            key: "memory",
            label: "Memory",
            percent: memPercent,
            level: getMetricHealthLevel(memPercent),
        },
        {
            key: "disk",
            label: "Disk",
            percent: diskPercent,
            level: getMetricHealthLevel(diskPercent),
        },
    ];
}

const PILL_VARIANTS: Record<HealthLevel, "success" | "warning" | "danger" | "neutral"> = {
    low: "success",
    medium: "warning",
    high: "danger",
    unknown: "neutral",
};

const LABELS: Record<HealthLevel, string> = {
    low: "Healthy",
    medium: "Watch",
    high: "Constrained",
    unknown: "N/A",
};

export function healthPillVariant(level: HealthLevel): "success" | "warning" | "danger" | "neutral" {
    return PILL_VARIANTS[level];
}

export function healthLevelLabel(level: HealthLevel): string {
    return LABELS[level];
}

const TOOLTIPS: Record<HealthLevel, string> = {
    low: "Everything's running smoothly",
    medium: "Usage is elevated — things may start to slow down",
    high: "This environment may become unresponsive",
    unknown: "Live metrics are still warming up",
};

export function healthLevelTooltip(level: HealthLevel): string {
    return TOOLTIPS[level];
}
